import { computed, unref } from 'vue'

import { useContext } from '@/composables/context'
import { tripQueryKeys } from '@/composables/trip'
import { useMutation, useQuery, useQueryClient } from '@/composables/vue-query'
import { eventModule } from '@/store'
import { useTransportationGroupStore } from '@/stores/transportation-group'
import type {
  TransportationGroup,
  TransportationGroupCreateInput,
  TransportationGroupUpdateInput,
  TransportationGroupUpdateResult,
} from '@/types/transportation'
import { MaybeRef } from '@/types/types'

export const transportationGroupQueryKeys = {
  all: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.detail(tripId), 'transportation-groups'] as const,
}

export function useTransportationReuseEnabled() {
  const { $config } = useContext()
  return computed(() => !!$config?.enableTransportationReuse)
}

function invalidateTripQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  tripId: MaybeRef<string>,
) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: tripQueryKeys.summary(tripId) }),
    queryClient.invalidateQueries({
      queryKey: tripQueryKeys.itinerary(tripId),
    }),
    queryClient.invalidateQueries({
      queryKey: tripQueryKeys.detail(tripId),
    }),
  ])
}

export function useTransportationGroups(tripId: MaybeRef<string>) {
  const { $axios, $sentry } = useContext()
  const groupStore = useTransportationGroupStore()
  const reuseEnabled = useTransportationReuseEnabled()

  return useQuery({
    queryKey: computed(() => transportationGroupQueryKeys.all(tripId)),
    enabled: computed(() => reuseEnabled.value && !!unref(tripId)),
    queryFn: async () => {
      const { data } = await $axios.get<TransportationGroup[]>(
        `/api/trips/${unref(tripId)}/transportation-groups`,
      )
      groupStore.replaceAll(data)
      return data
    },
    onError: (error) => {
      $sentry.captureException(error)
      eventModule.newError('error.transportation.fetch')
    },
  })
}

export function useCreateTransportationGroup(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()
  const groupStore = useTransportationGroupStore()

  // No onError Sentry capture here: the only caller is syncGroupSelection,
  // which always runs inside commitSave's try/catch (TransportationForm.vue),
  // where the failure is captured once. Capturing here too would double-report.
  return useMutation({
    mutationFn: async (
      input: TransportationGroupCreateInput,
    ): Promise<TransportationGroup> => {
      const { data } = await $axios.post<TransportationGroup>(
        `/api/trips/${unref(tripId)}/transportation-groups`,
        input,
      )
      groupStore.setGroup(data)
      return data
    },
    onSuccess: async () => {
      await invalidateTripQueries(queryClient, tripId)
    },
  })
}

export function useUpdateTransportationGroup(tripId: MaybeRef<string>) {
  const { $axios, $sentry } = useContext()
  const queryClient = useQueryClient()
  const groupStore = useTransportationGroupStore()

  return useMutation({
    mutationFn: async (params: {
      groupId: string
      input: TransportationGroupUpdateInput
    }): Promise<TransportationGroupUpdateResult> => {
      const { data } = await $axios.patch<TransportationGroupUpdateResult>(
        `/api/trips/${unref(tripId)}/transportation-groups/${params.groupId}`,
        params.input,
      )
      if (data.kind === 'demoted') {
        groupStore.removeGroup(data.groupId)
      } else if (data.kind === 'updated') {
        groupStore.setGroup(data.group)
      } else {
        const error = new Error(
          `Unexpected transportation group update result: ${JSON.stringify(
            data,
          )}`,
        )
        $sentry.captureException(error)
        throw error
      }
      return data
    },
    onSuccess: async () => {
      await invalidateTripQueries(queryClient, tripId)
    },
    // No onError Sentry capture: like the create mutation, the only caller is
    // syncGroupSelection inside commitSave's try/catch, which captures once.
    // The unexpected-kind branch above still captures its own contextual error.
  })
}

export function useDeleteTransportationGroup(tripId: MaybeRef<string>) {
  const { $axios, $sentry } = useContext()
  const queryClient = useQueryClient()
  const groupStore = useTransportationGroupStore()

  return useMutation({
    mutationFn: async (params: { groupId: string; keepSegments?: boolean }) => {
      await $axios.delete(
        `/api/trips/${unref(tripId)}/transportation-groups/${params.groupId}`,
        { params: { keepSegments: params.keepSegments ? 'true' : 'false' } },
      )
      groupStore.removeGroup(params.groupId)
    },
    onSuccess: async () => {
      await invalidateTripQueries(queryClient, tripId)
    },
    onError: (error) => {
      $sentry.captureException(error)
    },
  })
}
